/**
 * SecureEmbed Energy Portal - ProvenanceChain Component
 *
 * Vertical timeline tree for provenance chain visualisation
 */

import React from 'react';
import { CheckCircle2, Database, Cpu, GitBranch, Sparkles } from 'lucide-react';
import DataSourceBadge from './DataSourceBadge';

interface ProvenanceNode {
  id: string;
  embedding_id: string;
  stage: string;
  timestamp: string;
  algorithm?: string;
  source_datasource?: string;
  confidence?: number;
  parent_id?: string;
  metadata?: Record<string, any>;
}

interface ProvenanceChainProps {
  chain: ProvenanceNode[];
}

const STAGE_CONFIG: Record<string, { icon: React.FC<any>; color: string; bg: string }> = {
  ingestion: { icon: Database, color: 'text-blue-600', bg: 'bg-blue-100' },
  embedding: { icon: Cpu, color: 'text-purple-600', bg: 'bg-purple-100' },
  indexing: { icon: GitBranch, color: 'text-green-600', bg: 'bg-green-100' },
  insight: { icon: Sparkles, color: 'text-amber-600', bg: 'bg-amber-100' },
  validation: { icon: CheckCircle2, color: 'text-teal-600', bg: 'bg-teal-100' },
};

const ProvenanceChain: React.FC<ProvenanceChainProps> = ({ chain }) => {
  if (chain.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No provenance chain data</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

      <div className="space-y-6">
        {chain.map((node, index) => {
          const config = STAGE_CONFIG[node.stage] || STAGE_CONFIG.ingestion;
          const Icon = config.icon;
          const isLast = index === chain.length - 1;

          return (
            <div key={node.id} className="relative flex gap-4">
              {/* Timeline dot */}
              <div className={`relative z-10 w-12 h-12 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0 border-4 border-white shadow-sm`}>
                <Icon className={`w-5 h-5 ${config.color}`} />
              </div>

              {/* Content */}
              <div className={`flex-1 bg-white rounded-xl border border-gray-200 p-4 shadow-sm ${isLast ? 'border-2 border-purple-200' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900 capitalize">{node.stage.replace(/_/g, ' ')}</h4>
                  <span className="text-xs text-gray-400">
                    {new Date(node.timestamp).toLocaleString()}
                  </span>
                </div>

                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">ID:</span>
                    <span className="font-mono text-xs text-gray-700">{node.id}</span>
                  </div>

                  {node.algorithm && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Algorithm:</span>
                      <span className="text-gray-700">{node.algorithm}</span>
                    </div>
                  )}

                  {node.source_datasource && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Source:</span>
                      <DataSourceBadge source={node.source_datasource} />
                    </div>
                  )}

                  {node.confidence != null && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Confidence:</span>
                      <div className="flex items-center gap-2 flex-1">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[120px]">
                          <div
                            className="h-full bg-[#2B064F] rounded-full"
                            style={{ width: `${node.confidence * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-700">
                          {(node.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProvenanceChain;
